import { FullStory } from '@fullstory/browser';
import { Icon, IconNames } from "@triumph/design-system";
import { useTranslation } from "react-i18next";
import { useAppContext } from "@/app/components/providers/AppContextProvider";
import { useEffect, useState } from "react";
import { PaddedCard } from "./BillingReminder.StyledComponents";
import {
  BillingReminder,
  InvoicesPastDueResponse,
  InvoicesPendingDueResponse,
} from "./billingReminder.types";
import { useGetInvoicesPastDueV2 } from "@/app/shared/api/endpoints/invoicePastDue";
import { updateBulkInvoiceCollectionStatus } from "@/app/shared/api/endpoints/BulkInvoiceCollectionStatus";
import { updateBillingReminderFeedback } from "@/app/shared/api/endpoints/updateBillingReminderFeedback";
import {
  formatDate,
  formatDateForSubmission,
  formatMoney,
} from "@/app/shared/utils/tableUtils";
import {
  buildBulkPaymentPayload,
  buildBulkWillNotPayPayload,
} from "@/app/shared/utils/invoiceUtils/invoiceTransformers";
import { BillingReminderFooter } from "./BillingReminderFooter";
import { BillingReminderHeader } from "./BillingReminderHeader";
import { HeaderCenter, CenterParagraph } from "./BillingReminderHeader/BillingReminderHeader.StyledComponents";
import { TextboxModal } from "@/app/shared/components/TextboxModal/TextboxModal";
import { PendingInvoicesTable } from "./PendingInvoicesTable";
import { PastDueInvoicesTable } from "./PastDueInvoicesTable";
import { AllInvoicesTable } from "./AllInvoicesTable";
import { useGetInvoicesPendingDueV2 } from "@/app/shared/api/endpoints/invoicesPendingDue";
import { clearSessionStorage, clearSessionStorageOnCompletion, readSavedToken, readSavedUrlToken, readV2Enabled } from "@/app/shared/utils/sessionStorage";
import { PageRoutes } from "@/app/components/router/routes";
import { configureInstanceAsync, getTokenAsync, getUrlToken } from "@/app/shared/api/endpoints/auth";
import { useNavigate } from "react-router-dom";
import { UpdatePaymentStatusModal } from "@/app/shared/components/UpdatePaymentStatusModal/UpdatePaymentStatusModal";
import { useModal } from "@/app/shared/hooks/useModal";
import { useBillingRemindersTable } from "@/app/shared/hooks/useBillingRemindersTable";

export const InvoiceBillingReminder: React.FC = () => {
  const { t } = useTranslation("billingReminder");
  const navigate = useNavigate();
  const { theme } = useAppContext();
  const [isV2, setIsV2] = useState(() => readV2Enabled());
  const [modalInputValue, setModalInputValue] = useState("");
  const [modalTextValue, setModalTextValue] = useState("");
  const [paymentLinkUrl, setPaymentLinkUrl] = useState("");
  const [isModalVisible, toggleModal] = useState(false);
  const [showDisputedReasons, toggleShowDisputedReasons] = useState(false);
  const [modalDisputedReasonValue, setModalDisputedReasonValue] = useState("");
  const [showUnableToSubmitMessage, toggleUnableToSubmitMessage] = useState(false);
  const [isSubmitted, toggleSubmitted] = useState(false);
  const [isSubmitDisabled, toggleSubmitDisabled] = useState(false);
  const [isInitialized, setInitialized] = useState(false);
  const [isTokenReady, setTokenReady] = useState(false);
  const [rows, setRowData] = useState<BillingReminder[]>([]);
  const [showPastDueInvoicesTable, toggleShowPastDueInvoicesTable] = useState(true);
  const [invoicesSubmitted, setNumberOfInvoicesSubmitted] = useState(0);
  const { refetch: refetchPastInvoiceData } = useGetInvoicesPastDueV2({ pageSize: 10, isCsv: false });
  const { refetch: refetchPendingInvoiceData } = useGetInvoicesPendingDueV2({ pageSize: 10, isCsv: false });
  const [currentInvoiceData, setCurrentInvoiceData] = useState<InvoicesPastDueResponse | InvoicesPendingDueResponse>();

  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const {
    isOpen: isPayDateModalOpen,
    openModal: openPayDateModal,
    closeModal: closePayDateModal,
  } = useModal();
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  const billingRemindersTable = useBillingRemindersTable({ enabled: isV2 && isTokenReady });

  const fetchInvoices = async () => {
    let response;
    if (showPastDueInvoicesTable) {
      response = await refetchPastInvoiceData();
    } else {
      response = await refetchPendingInvoiceData();
    }
    setCurrentInvoiceData(response.data);
  };

  useEffect(() => {
    if (isV2) {
      setInitialized(true);
    }
  }, [isV2]);

  useEffect(() => {
    if (!isV2 && isTokenReady) {
      fetchInvoices();
    }
  }, [showPastDueInvoicesTable, isTokenReady]);

  useEffect(() => {
    if (!isV2 && currentInvoiceData) {
      if (showPastDueInvoicesTable && currentInvoiceData.totalCount === 0) {
        setCurrentInvoiceData(undefined);
        toggleShowPastDueInvoicesTable(false);
        return;
      } else if (!showPastDueInvoicesTable && currentInvoiceData.totalCount === 0) {
        toggleSubmitted(true);
        clearSessionStorageOnCompletion();
      }

      const invoices = currentInvoiceData.items;
      const cleanedData: BillingReminder[] = invoices.map(
        (invoice: BillingReminder) => ({
          ...invoice,
          willPay: invoice.willPay ?? true,
          paymentDate: invoice.paymentDate,
          invoiceDate: formatDate(invoice.invoiceDate),
          invoiceDueDate: formatDate(invoice.invoiceDueDate),
          amount: formatMoney(invoice.amount),
          amountDue: formatMoney(invoice.amountDue),
          showWarning: showPastDueInvoicesTable,
        })
      );
      if (currentInvoiceData.paymentLinkUrl) {
        setPaymentLinkUrl(currentInvoiceData.paymentLinkUrl);
      }

      setRowData(cleanedData);
      validateFormFields(cleanedData, showPastDueInvoicesTable);
      setInitialized(true);
    }
  }, [currentInvoiceData, showPastDueInvoicesTable]);

  useEffect(() => {
    initializePage();
  }, []);

  const initializePage = async () => {
    const currentUrlToken = getUrlToken();
    const storedUrlToken = readSavedUrlToken();
    const storedToken = readSavedToken();

    const canUseCachedToken = storedToken &&
                              storedUrlToken &&
                              currentUrlToken === storedUrlToken;

    if (canUseCachedToken) {
      await configureInstanceAsync(storedToken);
      setIsV2(readV2Enabled());
      setTokenReady(true);
      if (currentUrlToken) {
        FullStory('setIdentity', { uid: currentUrlToken});
      }
    } else {
      clearSessionStorage();
      try {
        const response = await getTokenAsync();
        if (!response?.data) {
          navigate(PageRoutes.AUTH_ERROR);
          return;
        }
        setIsV2(readV2Enabled());
        setTokenReady(true);
        const brToken = getUrlToken();
        if (brToken) {
          FullStory('setIdentity', { uid: brToken });
        }
      } catch (error: any) {
        navigate(PageRoutes.AUTH_ERROR);
      }
    }
  };

  const validateFormFields = (rows: BillingReminder[], isPastDue: boolean) => {
    const updatedRows = rows.map((row) => ({
      ...row,
      showWarning: isPastDue && row.paymentDate == null && row.disputedReasonId == null,
    }));

    toggleSubmitDisabled(
      updatedRows.some(
        (row) => row.disputedReasonId == null && row.paymentDate == null
      ) || updatedRows.length <= 0
    );

    return updatedRows;
  };

  const handlePaymentDateChange = (
    invoiceNumber: string,
    selectedPaymentDate: string | undefined
  ) => {
    const updatedRows = rows.map((row) =>
      row.invoiceNumber === invoiceNumber
        ? {
            ...row,
            paymentDate: selectedPaymentDate ? formatDateForSubmission(selectedPaymentDate) : undefined,
            willPay: selectedPaymentDate ? true : false,
            disputedReasonId: selectedPaymentDate ? undefined : row.disputedReasonId,
            reason: selectedPaymentDate ? undefined : row.reason,
            userText: row.userText,
          }
        : row
    );

    const validatedRows = validateFormFields(updatedRows, showPastDueInvoicesTable);
    setRowData(validatedRows);
  };

  const handleNotesChange = (invoiceNumber: string, includeDisputedReasons: boolean) => {
    const row = rows.find((row) => row.invoiceNumber === invoiceNumber);
    if (includeDisputedReasons) {
      toggleShowDisputedReasons(true);
    }
    setModalDisputedReasonValue(row?.disputedReasonId ?? "");
    setModalInputValue(invoiceNumber);
    setModalTextValue(row?.userText ?? "");
    toggleModal(true);
  };

  const handleModalSubmit = (
    invoiceNumber: string,
    value: string,
    disputedReasonId?: string,
    disputedReasonDesc?: string
  ) => {
    const updatedRows = rows.map((row) =>
      row.invoiceNumber === invoiceNumber
        ? {
            ...row,
            userText: value,
            disputedReasonId: disputedReasonId ? disputedReasonId : undefined,
            reason: disputedReasonDesc ? disputedReasonDesc : undefined,
            willPay: disputedReasonId ? false : row.willPay,
            paymentDate: disputedReasonId ? undefined : row.paymentDate,
          }
        : row
    );

    const validatedRows = validateFormFields(updatedRows, showPastDueInvoicesTable);
    setRowData(validatedRows);
    setModalInputValue("");
    setModalTextValue("");
    toggleShowDisputedReasons(false);
    toggleModal(false);
  };

  const handleModalClose = () => {
    setModalInputValue("");
    setModalTextValue("");
    toggleShowDisputedReasons(false);
    toggleModal(false);
  };

  const handleFormSubmit = async () => {
    toggleSubmitDisabled(true);

    const formattedRows = rows.map((row) => {
      return {
        invoiceId: row.invoiceId,
        disputedReasonId: row.disputedReasonId,
        willPay: row.willPay,
        userText: row.userText ?? "",
        paymentDate: row.paymentDate,
        urgent: row.urgent ?? false,
      };
    });

    await updateBulkInvoiceCollectionStatus(formattedRows)
      .then(() => {
        if (currentInvoiceData?.hasMore) {
          fetchInvoices();
          setNumberOfInvoicesSubmitted(invoicesSubmitted + 10);
        } else if (showPastDueInvoicesTable) {
          setNumberOfInvoicesSubmitted(invoicesSubmitted + (currentInvoiceData?.totalCount ?? 0));
          setCurrentInvoiceData(undefined);
          setRowData([]);
          toggleShowPastDueInvoicesTable(false);
        } else {
          setNumberOfInvoicesSubmitted(invoicesSubmitted + (currentInvoiceData?.totalCount ?? 0));
          toggleSubmitted(true);
          clearSessionStorageOnCompletion();
        }
      })
      .catch(() => {
        toggleSubmitDisabled(false);
        return Promise.reject(t("submission.error"));
      });
  };

  const handleSelectInvoice = (invoiceNumber: string) => {
    setSelectedInvoices((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceNumber)) {
        next.delete(invoiceNumber);
      } else {
        next.add(invoiceNumber);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const pageInvoiceNumbers = billingRemindersTable.rows.map((r) => r.invoiceNumber);
    const allOnPageSelected = pageInvoiceNumbers.every((n) => selectedInvoices.has(n));
    if (allOnPageSelected) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(pageInvoiceNumbers));
    }
  };

  const submitBulkFeedback = async (invoiceNumbers: string[], payload: any[]): Promise<void> => {
    setIsBulkSubmitting(true);
    try {
      await updateBillingReminderFeedback(payload);
      setSelectedInvoices(new Set());
      const { data: refreshed } = await billingRemindersTable.refetch();
      if ((refreshed?.totalCount ?? 0) === 0) {
        clearSessionStorageOnCompletion();
        toggleSubmitted(true);
      }
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  const handleBulkPayDateSubmit = async (invoiceNumbers: string[], date: string, note: string = ''): Promise<void> => {
    const matchedRows = billingRemindersTable.rows.filter((r) => invoiceNumbers.includes(r.invoiceNumber)) as any[];
    await submitBulkFeedback(invoiceNumbers, buildBulkPaymentPayload(matchedRows, date, note));
  };

  const handleBulkWillNotPaySubmit = async (invoiceNumbers: string[], reasonId: string, reasonDesc: string, note: string): Promise<void> => {
    const matchedRows = billingRemindersTable.rows.filter((r) => invoiceNumbers.includes(r.invoiceNumber)) as any[];
    await submitBulkFeedback(invoiceNumbers, buildBulkWillNotPayPayload(matchedRows, reasonId, reasonDesc, note));
  };

  const skipOptionalInvoices = () => {
    toggleSubmitted(true);
    clearSessionStorageOnCompletion();
  };

  const showPendingInvoicesTable = !showPastDueInvoicesTable && !isSubmitted && rows.length > 0;

  const v2Rows = billingRemindersTable.rows;

  return (
    <div>
      {isInitialized ? (
        <div>
          <PaddedCard isLarge={true}>
            {isV2 ? null : (
              <BillingReminderHeader
                isSubmitted={isSubmitted}
                paymentLinkUrl={paymentLinkUrl}
                invoicesSubmitted={invoicesSubmitted}
                showUnableToSubmitMessage={showUnableToSubmitMessage}
              />
            )}
            {isV2 && isSubmitted ? (
              <div>
                <HeaderCenter>{t("submission.thankYou")}</HeaderCenter>
                <CenterParagraph>
                  <Icon
                    color={theme.colors.semantic.successDefault}
                    name={IconNames.Success_Filled}
                    id={"success-check-icon"}
                  />
                  {t("submission.successMessage")}
                </CenterParagraph>
              </div>
            ) : isV2 ? (
              <AllInvoicesTable
                rows={v2Rows}
                totalInvoices={billingRemindersTable.totalCount}
                selectedInvoices={selectedInvoices}
                onSelectInvoice={handleSelectInvoice}
                onSelectAll={handleSelectAll}
                allSelected={v2Rows.length > 0 && v2Rows.every((r) => selectedInvoices.has(r.invoiceNumber))}
                someSelected={v2Rows.some((r) => selectedInvoices.has(r.invoiceNumber)) && !v2Rows.every((r) => selectedInvoices.has(r.invoiceNumber))}
                onUpdatePaymentStatus={openPayDateModal}
                paymentLinkUrl={billingRemindersTable.paymentLinkUrl}
                currentPage={billingRemindersTable.currentPage}
                pageSize={billingRemindersTable.pageSize}
                onPageChange={billingRemindersTable.handlePageChange}
                onPageSizeChange={billingRemindersTable.handlePageSizeChange}
                isLoading={billingRemindersTable.isLoading}
                handleOnSort={billingRemindersTable.handleOnSort}
                handleButtonFilterChange={billingRemindersTable.handleButtonFilterChange}
                availableFilterOptions={billingRemindersTable.availableFilterOptions}
                dateRangeDisplay={billingRemindersTable.dateRangeDisplay}
              />
            ) : showPastDueInvoicesTable ? (
              <PastDueInvoicesTable
                isSubmitDisabled={isSubmitDisabled}
                handleFormSubmit={handleFormSubmit}
                rows={rows}
                handleNotesChange={handleNotesChange}
                handlePaymentDateChange={handlePaymentDateChange}
                totalInvoices={currentInvoiceData?.totalCount ?? 0}
                toggleUnableToSubmitMessage={toggleUnableToSubmitMessage}
              />
            ) : showPendingInvoicesTable ? (
              <PendingInvoicesTable
                isSubmitDisabled={isSubmitDisabled}
                rows={rows}
                handleFormSubmit={handleFormSubmit}
                handleNotesChange={handleNotesChange}
                handlePaymentDateChange={handlePaymentDateChange}
                skipOptionalInvoices={skipOptionalInvoices}
                totalInvoices={currentInvoiceData?.totalCount ?? 0}
                toggleUnableToSubmitMessage={toggleUnableToSubmitMessage}
              />
            ) : null}
            <TextboxModal
              isVisible={isModalVisible}
              showDisputedReasons={showDisputedReasons}
              rows={rows}
              inputValue={modalInputValue}
              textValue={modalTextValue}
              reasonValue={modalDisputedReasonValue}
              handleClose={handleModalClose}
              handleSubmit={handleModalSubmit}
              setTextValue={setModalTextValue}
              setReasonValue={setModalDisputedReasonValue}
            />
            {isV2 && (
              <UpdatePaymentStatusModal
                isVisible={isPayDateModalOpen}
                handleClose={closePayDateModal}
                selectedInvoices={selectedInvoices}
                onBulkPaymentDateChange={handleBulkPayDateSubmit}
                onBulkWillNotPayChange={handleBulkWillNotPaySubmit}
                isSubmitting={isBulkSubmitting}
              />
            )}
          </PaddedCard>
          <BillingReminderFooter />
        </div>
      ) : null}
    </div>
  );
};
